import { CSSProperties, useState } from 'react';
import { httpsLink, makeKey, styx } from 'modules/Utils';
import CardSymphony from 'elements/CardSymphony';
import WebsiteBuilderHeader from '../WebsiteBuilderHeader';
import useWebsiteBuilder, {
    SOCIAL_PLATFORMS_WEBSITE_CONTENT,
    WebsiteBuilder_socialPlatformsExists
} from 'Hooks/useWebsiteBuilder';
import ButtonSymphony from 'elements/ButtonSymphony';
import PlatformLink from 'elements/PlatformLink';
import ModalSymphony from 'elements/ModalSymphony';
import Icon from 'elements/Icon';
import clsx from 'clsx';
import Colors from 'modules/Colors';
import { DraggableList } from 'components/shareable/DraggableList';
import WebsiteBuilderEmptyState from '../WebsiteBuilderEmptyState';
import { SocialPlatform, SOCIAL_PLATFORMS_INFO } from 'modules/Const';
import useScreen from 'Hooks/useScreen';
import WebsiteBuilderFooter from '../WebsiteBuilderFooter';

const DISABLED_OPACITY = 0.25;

interface SocialPlatformsContentProps {
    className: string;
    contentIndex: number;
    tabSelectorComponent: React.ReactNode;
    id: string;
}

const SocialPlatformsContent = ({
    className,
    contentIndex,
    tabSelectorComponent,
    id,
}: SocialPlatformsContentProps) => {
    const {
        mobileView,
    } = useScreen();

    const {
        websiteContent,
        setWebsiteContent,
    } = useWebsiteBuilder() as WebsiteBuilder_socialPlatformsExists;

    const socialPlatformsContent = websiteContent[contentIndex].socialPlatformsContent;
    const platforms = socialPlatformsContent.platforms;

    const [showAddPlatformModal, setShowAddPlatformModal] = useState<boolean>(false);
    const [showSocialPlatformsModal, setShowSocialPlatformsModal] = useState<boolean>(false);

    const renderRightSideContent = () => {
        if (mobileView) {
            return (
                <Icon
                    className="socialPlatformsContentAddPlatformButtonIcon"
                    icon="addCircle"
                    size={24}
                    onClick={() => setShowAddPlatformModal(true)}
                />
            )
        }

        return (
            <ButtonSymphony
                className="socialPlatformsContentAddPlatformButton"
                iconLeft="addCircle"
                onClick={() => setShowAddPlatformModal(true)}
                variant="text"
            >
                Edit Platform
            </ButtonSymphony>
        )
    }

    const renderSocialPlatforms = () => {
        return (
            <div
                className="socialPlatformsContentPlatformsContainer"
                style={sty.socialPlatformsContentPlatformsContainer}
            >
                {platforms.map((platform, index) => {
                    const platformName = platform.name;
                    const platformInfo = SOCIAL_PLATFORMS_INFO[platformName];
                    if (!platformInfo) {
                        return null
                    }

                    const {
                        iconColor,
                        backgroundColor,
                        title,
                    } = platformInfo;

                    return (
                        <div
                            key={makeKey(platform.name, index)}
                            className="socialPlatformsContentPlatform"
                            style={styx(
                                sty.socialPlatformsContentPlatform,
                                {
                                    backgroundColor,
                                    opacity: platform.isEnabled ? 1 : DISABLED_OPACITY,
                                }
                            )}
                            onClick={() => setShowSocialPlatformsModal(true)}
                        >
                            <Icon
                                className="socialPlatformsContentPlatformIcon"
                                icon={iconColor || ''}
                                size={28}
                            />
                            <div>{title}</div>
                            <Icon
                                className="socialPlatformsContentPlatformIcon"
                                icon="edit"
                                size={22}
                                color={Colors.greyDark}
                            />
                        </div>
                    )
                })}
            </div>
        )
    }

    // NOTE: render()
    return (
        <>
            <CardSymphony
                className={clsx("socialPlatformsContentComponent", className)}
                style={styx(sty.socialPlatformsContentComponent)}
                size="small"
                id={id}
            >
                <WebsiteBuilderHeader
                    contentIndex={contentIndex}
                    rightSideContent={
                        <>
                            {tabSelectorComponent}
                            {renderRightSideContent()}
                        </>
                    }
                />
                {!platforms.length &&
                    <WebsiteBuilderEmptyState
                        className="socialPlatformsContentEmptyState"
                        title="No social platforms added"
                        description="Add a social platform to your website to get started."
                        isHorizontal
                        buttonText="Add Platform"
                        onClickButton={() => setShowAddPlatformModal(true)}
                    />
                }

                {renderSocialPlatforms()}

                <WebsiteBuilderFooter
                    className="socialPlatformsContentFooter"
                    contentIndex={contentIndex}
                    isBackgroundColor
                    isTextColor
                />
            </CardSymphony>

            {/* MODALS */}
            <ModalSymphony
                className="socialPlatformsContentAddPlatformModal"
                isOpen={showAddPlatformModal}
                onClose={() => setShowAddPlatformModal(false)}
                size="small"
                title="Add Social Platform"
                style={{ gap: 8 }}
            >
                {SOCIAL_PLATFORMS_WEBSITE_CONTENT.map((platform, index) => {
                    const isPlatformAdded = platforms.some(p => p.name === platform.name);

                    const {
                        iconColor,
                        backgroundColor,
                        title,
                    } = SOCIAL_PLATFORMS_INFO[platform.name] || {};

                    return (
                        <div
                            key={makeKey(platform.name, index)}
                            className={clsx(
                                'socialPlatformsContentAddPlatformModalPlatform',
                                { socialPlatformsContentAddPlatformModalPlatformDisabled: isPlatformAdded }
                            )}
                            style={styx(
                                sty.socialPlatformsContentAddPlatformModalPlatform,
                                isPlatformAdded && sty.socialPlatformsContentAddPlatformModalPlatformDisabled,
                                { backgroundColor }
                            )}
                            onClick={() => {
                                if (isPlatformAdded) {
                                    return;
                                }

                                setShowAddPlatformModal(false);
                                const websiteContentLocal = [...websiteContent];
                                websiteContentLocal[contentIndex].socialPlatformsContent.platforms.push({
                                    ...platform,
                                    title,
                                    icon: iconColor || '',
                                    description: title,
                                    isEnabled: true,
                                });
                                setWebsiteContent(websiteContentLocal);
                            }}
                        >
                            <Icon
                                className="socialPlatformsContentAddPlatformModalIcon"
                                icon={iconColor || ''}
                                size={28}
                                style={isPlatformAdded ? { opacity: 0.5 } : undefined}
                            />
                            {title}
                            {isPlatformAdded &&
                                <span style={sty.alreadyAddedText}>
                                    Already added
                                </span>
                            }
                        </div>
                    )
                })}
            </ModalSymphony>

            <ModalSymphony
                className="socialPlatformsContentSocialPlatformsModal"
                isOpen={showSocialPlatformsModal}
                onClose={() => setShowSocialPlatformsModal(false)}
                title="Social Platforms"
                subtitle="Edit your social platforms"
                onClickButtonRight={() => {
                    setShowSocialPlatformsModal(false);
                }}
                buttonRightText="Done"
                onClickButtonLeft={() => {
                    setShowAddPlatformModal(true);
                }}
                buttonLeftText="Add Platform"
                buttonLeftVariant="outlined"
            >
                <DraggableList
                    id={`socialPlatformsContentDraggableList-${socialPlatformsContent.title}`}
                    list={platforms}
                    keyOfItem="name"
                    isDragHandle
                    updateList={(newList) => {
                        const websiteContentLocal = [...websiteContent];
                        websiteContentLocal[contentIndex].socialPlatformsContent.platforms = newList;
                        setWebsiteContent(websiteContentLocal);
                    }}
                    renderItem={(platform, index, dragHandleProps) => {

                        const {
                            iconColor,
                            backgroundColor,
                            title,
                        } = SOCIAL_PLATFORMS_INFO[platform.name] || {};

                        return (
                            <div
                                className="socialPlatformsContentPlatformLinkContainer"
                                style={styx(
                                    sty.socialPlatformsContentPlatformLinkContainer,
                                    {
                                        backgroundColor,
                                        opacity: platform.isEnabled ? 1 : DISABLED_OPACITY,
                                    }
                                )}
                            >
                                <div
                                    {...dragHandleProps}
                                    className="socialPlatformsContentPlatformLinkDragHandle"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'grab',
                                    }}
                                >
                                    <Icon
                                        className="socialPlatformsContentPlatformLinkDragHandleIcon"
                                        icon="drag"
                                        size={24}
                                        color={Colors.grey}
                                    />
                                </div>
                                <PlatformLink
                                    key={makeKey(platform.name, index, contentIndex)}
                                    className="socialPlatformsContentPlatformLink"
                                    title={title}
                                    description={platform.description}
                                    icon={iconColor || ''}
                                    link={platform.link}
                                    isEnabled={platform.isEnabled}
                                    placeholder={platform.placeholder}
                                    isLabel={false}
                                    onBlur={(newLink: string) => {
                                        if(platform.name === SocialPlatform.EMAIL) {
                                            return;
                                        }
                                        const websiteContentLocal = [...websiteContent];
                                        websiteContentLocal[contentIndex].socialPlatformsContent.platforms[index].link = httpsLink(newLink);
                                        setWebsiteContent(websiteContentLocal);
                                    }}
                                    onRemove={() => {
                                        const websiteContentLocal = [...websiteContent];
                                        websiteContentLocal[contentIndex].socialPlatformsContent.platforms.splice(index, 1);
                                        setWebsiteContent(websiteContentLocal);
                                    }}
                                    onChange={(newLink: string) => {
                                        const websiteContentLocal = [...websiteContent];
                                        websiteContentLocal[contentIndex].socialPlatformsContent.platforms[index].link = newLink;
                                        setWebsiteContent(websiteContentLocal);
                                    }}
                                    onToggleEnabled={() => {
                                        const websiteContentLocal = [...websiteContent];
                                        websiteContentLocal[contentIndex].socialPlatformsContent.platforms[index].isEnabled =
                                            !websiteContentLocal[contentIndex].socialPlatformsContent.platforms[index].isEnabled;
                                        setWebsiteContent(websiteContentLocal);
                                    }}
                                />
                                {/* <Icon
                                    className="socialPlatformsContentPlatformLinkUpIcon"
                                    icon="chevronUp"
                                    size={24}
                                    color={index === 0 ? Colors.greyDark : Colors.purple}
                                    onClick={() => {
                                        if (index === 0) {
                                            return;
                                        }
                                        const websiteContentLocal = [...websiteContent];
                                        websiteContentLocal[contentIndex].socialPlatformsContent.platforms[index] = websiteContentLocal[contentIndex].socialPlatformsContent.platforms[index - 1];
                                        websiteContentLocal[contentIndex].socialPlatformsContent.platforms[index - 1] = platform;
                                        setWebsiteContent(websiteContentLocal);
                                    }}
                                />
                                <Icon
                                    className="socialPlatformsContentPlatformLinkDownIcon"
                                    icon="chevronDown"
                                    size={24}
                                    color={index === platforms.length - 1 ? Colors.greyDark : Colors.purple}
                                    onClick={() => {
                                        if (index === platforms.length - 1) {
                                            return;
                                        }
                                        const websiteContentLocal = [...websiteContent];
                                        websiteContentLocal[contentIndex].socialPlatformsContent.platforms[index] = websiteContentLocal[contentIndex].socialPlatformsContent.platforms[index + 1];
                                        websiteContentLocal[contentIndex].socialPlatformsContent.platforms[index + 1] = platform;
                                        setWebsiteContent(websiteContentLocal);
                                    }}
                                /> */}
                            </div>
                        )
                    }}
                />
            </ModalSymphony>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    socialPlatformsContentComponent: {
    },
    socialPlatformsContentAddPlatformModalPlatform: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        gap: 8,
        padding: 16,
        borderRadius: 16,
    },
    socialPlatformsContentAddPlatformModalPlatformDisabled: {
        cursor: 'not-allowed',
        opacity: 0.5,
    },
    alreadyAddedText: {
        marginLeft: 'auto',
        fontSize: 14,
    },
    socialPlatformsContentPlatformLinkContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: 8,
        borderRadius: 8,
    },
    socialPlatformsContentPlatformsContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        flexWrap: 'wrap',
    },
    socialPlatformsContentPlatform: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 8,
        borderRadius: 16,
        backgroundColor: Colors.greyLight,
    },
}

export default SocialPlatformsContent;